import React, { useState } from 'react';
import {
  Container,
  Typography,
  Button,
  TextField,
  Paper,
  Box,
  Card,
  CardContent,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Business as BusinessIcon,
  NetworkCheck as NetworkIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';

// Network Intelligence Platform Theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2', // Professional blue
    },
    secondary: {
      main: '#dc004e', // Accent red
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h2: {
      fontWeight: 700,
    },
    h4: {
      fontWeight: 600,
    },
  },
});

const HeroSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  padding: theme.spacing(8, 0),
  textAlign: 'center',
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

interface SignupFormData {
  // Basic Information
  tenant_id: string;
  name: string;
  company_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;

  // Business Information
  industry: string;
  company_size: string;
  website: string;

  // Technical Information
  source_ips: string;
  network_devices: string;
  protocols_used: string[];
  current_monitoring: string;
  pain_points: string;
  integration_requirements: string;

  // Additional
  notes: string;
}

function App() {
  const [formData, setFormData] = useState<SignupFormData>({
    tenant_id: '',
    name: '',
    company_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    industry: '',
    company_size: '',
    website: '',
    source_ips: '',
    network_devices: '',
    protocols_used: [],
    current_monitoring: '',
    pain_points: '',
    integration_requirements: '',
    notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (field: keyof SignupFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleProtocolChange = (protocol: string) => {
    setFormData(prev => ({
      ...prev,
      protocols_used: prev.protocols_used.includes(protocol)
        ? prev.protocols_used.filter(p => p !== protocol)
        : [...prev.protocols_used, protocol]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.tenant_id || !formData.name || !formData.contact_email) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create tenant with comprehensive data
      const tenantData = {
        ...formData,
        billing_tier: 'starter' as const,
        subscription_status: 'trial' as const,
        trial_ends_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        status: 'active' as const
      };

      const response = await fetch('https://flux-admin-api.lorien.workers.dev/api/tenants', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tenantData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Registration failed');
      }

      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <ThemeProvider theme={theme}>
        <Container maxWidth="md" sx={{ mt: 8, mb: 4 }}>
          <Card sx={{ textAlign: 'center', p: 6 }}>
            <Typography variant="h3" color="primary" gutterBottom>
              🎉 Welcome to Flux Network Intelligence Platform!
            </Typography>
            <Typography variant="h5" gutterBottom>
              Your account has been created successfully
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
              Thank you for choosing Flux. Your 30-day free trial has started, and you can begin sending observability data immediately.
              You'll receive an email with your API keys and detailed setup instructions within the next few minutes.
            </Typography>
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" color="primary" gutterBottom>
                What's Next?
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                • Check your email for API credentials and setup guide<br/>
                • Review our documentation for integration options<br/>
                • Start sending your network observability data<br/>
                • Contact support if you need assistance
              </Typography>
            </Box>
          </Card>
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="md">
          <Typography variant="h2" component="h1" gutterBottom>
            Network Intelligence Platform
          </Typography>
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 400 }}>
            by Flux
          </Typography>
          <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
            Enterprise-grade network observability with AI-powered insights
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1.2rem', mb: 4 }}>
            Get 30 days free access to comprehensive network monitoring, anomaly detection,
            and root cause analysis for your infrastructure.
          </Typography>
        </Container>
      </HeroSection>

      <Container maxWidth="lg" sx={{ mt: -4, mb: 4 }}>
        {/* Features Overview */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mb: 6, justifyContent: 'center' }}>
          <FeatureCard sx={{ flex: '1 1 250px', maxWidth: 300 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <NetworkIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Multi-Protocol Support
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Syslog, SNMP, NetFlow, and custom protocols
              </Typography>
            </CardContent>
          </FeatureCard>
          <FeatureCard sx={{ flex: '1 1 250px', maxWidth: 300 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <AnalyticsIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                AI-Powered Analytics
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Advanced AI-driven insights with automated anomaly detection and intelligent root cause analysis for proactive network management
              </Typography>
            </CardContent>
          </FeatureCard>
          <FeatureCard sx={{ flex: '1 1 250px', maxWidth: 300 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <SecurityIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Enterprise Security
              </Typography>
              <Typography variant="body2" color="text.secondary">
                SOC 2 compliant with end-to-end encryption
              </Typography>
            </CardContent>
          </FeatureCard>
          <FeatureCard sx={{ flex: '1 1 250px', maxWidth: 300 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <BusinessIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Cost Effective
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Zero-egress pricing with Cloudflare's edge network
              </Typography>
            </CardContent>
          </FeatureCard>
        </Box>

        {/* About Section */}
        <Box sx={{ mb: 8, textAlign: 'center' }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
            About Flux
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, maxWidth: 800, mx: 'auto', color: 'text.secondary' }}>
            Our approach combines cutting-edge AI technology with enterprise-grade reliability to deliver unparalleled network observability.
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: 1000, mx: 'auto', lineHeight: 1.8 }}>
            At Flux, we believe that network intelligence should be accessible, affordable, and actionable.
            Our AI-powered platform transforms complex network data into clear, actionable insights that drive better decision-making and operational excellence.
            With our advanced anomaly detection and root cause analysis capabilities, network operators can proactively identify and resolve issues before they impact business operations.
          </Typography>
        </Box>

        {/* Signup Form */}
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', mb: 4 }}>
            Start Your Free Trial
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Basic Information */}
            <Box>
              <Typography variant="h6" color="primary" gutterBottom>
                📋 Basic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <TextField
                fullWidth
                sx={{ flex: '1 1 300px' }}
                label="Organization Name *"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />

              <TextField
                fullWidth
                sx={{ flex: '1 1 300px' }}
                label="Tenant ID *"
                value={formData.tenant_id}
                onChange={(e) => handleInputChange('tenant_id', e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                helperText="Unique identifier (lowercase, alphanumeric, hyphens, underscores only)"
                required
              />

              <TextField
                fullWidth
                sx={{ flex: '1 1 300px' }}
                label="Contact Name"
                value={formData.contact_name}
                onChange={(e) => handleInputChange('contact_name', e.target.value)}
              />

              <TextField
                fullWidth
                sx={{ flex: '1 1 300px' }}
                label="Email Address *"
                type="email"
                value={formData.contact_email}
                onChange={(e) => handleInputChange('contact_email', e.target.value)}
                required
              />

              <TextField
                fullWidth
                sx={{ flex: '1 1 300px' }}
                label="Phone Number"
                value={formData.contact_phone}
                onChange={(e) => handleInputChange('contact_phone', e.target.value)}
              />

              <TextField
                fullWidth
                sx={{ flex: '1 1 300px' }}
                label="Website"
                value={formData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
              />
            </Box>

            {/* Business Information */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" color="primary" gutterBottom>
                🏢 Business Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <TextField
                fullWidth
                sx={{ flex: '1 1 300px' }}
                label="Company Name"
                value={formData.company_name}
                onChange={(e) => handleInputChange('company_name', e.target.value)}
              />

              <FormControl fullWidth sx={{ flex: '1 1 300px' }}>
                <InputLabel>Industry</InputLabel>
                <Select
                  value={formData.industry}
                  label="Industry"
                  onChange={(e) => handleInputChange('industry', e.target.value)}
                >
                  <MenuItem value="technology">Technology</MenuItem>
                  <MenuItem value="finance">Finance</MenuItem>
                  <MenuItem value="healthcare">Healthcare</MenuItem>
                  <MenuItem value="retail">Retail</MenuItem>
                  <MenuItem value="manufacturing">Manufacturing</MenuItem>
                  <MenuItem value="telecommunications">Telecommunications</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ flex: '1 1 300px' }}>
                <InputLabel>Company Size</InputLabel>
                <Select
                  value={formData.company_size}
                  label="Company Size"
                  onChange={(e) => handleInputChange('company_size', e.target.value)}
                >
                  <MenuItem value="1-10">1-10 employees</MenuItem>
                  <MenuItem value="11-50">11-50 employees</MenuItem>
                  <MenuItem value="51-200">51-200 employees</MenuItem>
                  <MenuItem value="201-1000">201-1000 employees</MenuItem>
                  <MenuItem value="1000+">1000+ employees</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Technical Information */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" color="primary" gutterBottom>
                🔧 Technical Information
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Help us prepare your integration by providing technical details
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Box>

            <TextField
              fullWidth
              label="Source IP Addresses"
              multiline
              rows={2}
              value={formData.source_ips}
              onChange={(e) => handleInputChange('source_ips', e.target.value)}
              helperText="IP addresses that will send data to Flux (one per line)"
              placeholder="192.168.1.10&#10;10.0.0.15&#10;172.16.0.5"
            />

            <TextField
              fullWidth
              label="Network Devices"
              multiline
              rows={2}
              value={formData.network_devices}
              onChange={(e) => handleInputChange('network_devices', e.target.value)}
              helperText="Types of network devices you want to monitor"
              placeholder="Cisco routers, Ubiquiti switches, Juniper firewalls, etc."
            />

            <Box>
              <Typography variant="body2" gutterBottom>
                Protocols Used:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {['syslog', 'snmp', 'netflow', 'sflow', 'ipfix', 'custom'].map((protocol) => (
                  <Chip
                    key={protocol}
                    label={protocol.toUpperCase()}
                    onClick={() => handleProtocolChange(protocol)}
                    color={formData.protocols_used.includes(protocol) ? 'primary' : 'default'}
                    variant={formData.protocols_used.includes(protocol) ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            </Box>

            <TextField
              fullWidth
              label="Current Monitoring Tools"
              multiline
              rows={2}
              value={formData.current_monitoring}
              onChange={(e) => handleInputChange('current_monitoring', e.target.value)}
              helperText="What monitoring/observability tools are you currently using?"
              placeholder="Splunk, ELK Stack, SolarWinds, PRTG, custom scripts, etc."
            />

            <TextField
              fullWidth
              label="Pain Points & Requirements"
              multiline
              rows={3}
              value={formData.pain_points}
              onChange={(e) => handleInputChange('pain_points', e.target.value)}
              helperText="What challenges are you facing? What do you hope to achieve?"
              placeholder="Alert fatigue, slow troubleshooting, lack of visibility, compliance requirements, etc."
            />

            <TextField
              fullWidth
              label="Integration Requirements"
              multiline
              rows={2}
              value={formData.integration_requirements}
              onChange={(e) => handleInputChange('integration_requirements', e.target.value)}
              helperText="Any specific integration requirements or preferences?"
              placeholder="API endpoints, authentication methods, data formats, etc."
            />

            <TextField
              fullWidth
              label="Additional Notes"
              multiline
              rows={2}
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              helperText="Any other information that would help us support you"
            />

            {error && (
              <Alert severity="error">
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 2,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold'
              }}
            >
              {loading ? 'Creating Your Account...' : '🚀 Start Free Trial'}
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              By signing up, you agree to our Terms of Service and Privacy Policy.
              Your 30-day trial includes full access to all features.
            </Typography>
          </Box>
        </Paper>

        {/* Legal Footer */}
        <Box sx={{ mt: 8, pt: 4, borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Legal Information
          </Typography>

          <Typography variant="body2" sx={{ mb: 2, maxWidth: 800, mx: 'auto' }}>
            <strong>Cookies Policy:</strong> This website uses cookies to enhance your browsing experience, analyze site traffic, and personalize content.
            By using our website, you consent to our use of cookies in accordance with our Privacy Policy.
            You can manage your cookie preferences through your browser settings.
          </Typography>

          <Typography variant="body2" sx={{ mb: 2, maxWidth: 800, mx: 'auto' }}>
            <strong>Accessibility Statement:</strong> We are committed to ensuring digital accessibility for people with disabilities in accordance with the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA standards.
            If you experience any difficulties accessing our content, please contact our support team for assistance.
            We continuously work to improve accessibility and welcome feedback on how we can better serve all users.
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            © 2025 Flux Network Intelligence Platform. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </ThemeProvider>
  );
}

export default App;
